import i18n from '@maia/i18n';
import { IAttachmentEntity, TaskCreateDto } from '@maia/shared';
import { Button, Typography } from '@maia/ui';
import { fileToBase64 } from '@maia/utils';
import * as v from 'valibot';
import { useAppForm } from '../../form/dynamic-form/dynamic-form';
import { useGetFilterOptionData } from '../utils/get-filter-options';

interface CreateTaskForm {
  readonly linkedItem: {
    readonly guid: string;
    readonly type: 'customer' | 'insurance' | 'claim' | 'quotation';
  };

  readonly onSubmit: (
    value: ConstructorParameters<typeof TaskCreateDto>[number],
  ) => Promise<void>;
}

type FormOptions = Omit<
  ConstructorParameters<typeof TaskCreateDto>[number],
  'description' | 'attachments'
> & {
  description: {
    markdown: string;
    files: Array<File>;
  };
};

export function CreateTaskForm(props: CreateTaskForm) {
  const { linkedItem, onSubmit } = props;
  const { brokers, teams } = useGetFilterOptionData();

  const form = useAppForm({
    defaultValues: {
      title: '',
      duration: 1,
      description: {
        markdown: '',
        files: [],
      },
      attachments: [],
      assignee: undefined,
      externally_visible: false,
      team: undefined,
      priority: 'medium',
      linked_item: {
        guid: linkedItem.guid,
        type: linkedItem.type,
      },
    } as FormOptions,
    onSubmit: async ({ value }) => {
      const files: Array<IAttachmentEntity> = await Promise.all(
        value.description.files.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            filename: file.name,
            mimetype: file.type,
            encoded_document: base64,
          };
        }),
      );

      onSubmit({
        title: value.title,
        duration: value.duration,
        description: value.description.markdown,
        attachments: files,
        externally_visible: value.externally_visible,
        team: value.team,
        priority: value.priority,
        linked_item: {
          guid: value.linked_item.guid,
          type: value.linked_item.type,
        },
        assignee: value.assignee,
      });
    },
  });

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <fieldset className="area-page-content col-span-full flex flex-col gap-y-32">
        <form.AppField
          name="title"
          validators={{
            onChange: v.pipe(
              v.string('Titel is verplicht'),
              v.trim(),
              v.minLength(1, 'Titel is verplicht'),
            ),
          }}
        >
          {(field) => <field.TextField label="Type taak" />}
        </form.AppField>
        <form.AppField
          name="duration"
          validators={{
            onChange: v.pipe(
              v.number('Verwachte oplostijd is verplicht'),
              v.minValue(1, 'Vewachte oplostijd moet minimaal 1 dag zijn'),
            ),
          }}
        >
          {(field) => <field.NumberField label="Verwachte oplostijd" />}
        </form.AppField>
        <form.AppField
          name="description"
          validators={{
            onChange: v.object({
              markdown: v.pipe(
                v.string('Toelichting is verplicht'),
                v.trim(),
                v.minLength(1, 'Toelichting is verplicht'),
              ),
              files: v.array(v.file()),
            }),
          }}
        >
          {(field) => <field.RichtTextEditorField label="Omschrijving" />}
        </form.AppField>
      </fieldset>
      <fieldset className="area-page-sidebar flex flex-col gap-y-24">
        <Typography variant="display6" weight="medium">
          Categoriseren
        </Typography>
        <form.AppField
          name="priority"
          validators={{
            onChange: v.string('Veld is verplicht'),
          }}
        >
          {(field) => (
            <field.SelectField items={priorityOptions} label="Prioriteit" />
          )}
        </form.AppField>
        <form.AppField
          name="team"
          validators={{
            onChange: v.string('Veld is verplicht'),
          }}
        >
          {(field) => (
            <field.SelectField
              label="Team"
              placeholder="Kies een team"
              defaultValue={field.state.value}
              items={teams.map((team) => ({
                value: team.guid,
                label: team.name,
                meta: {},
              }))}
            />
          )}
        </form.AppField>
        <form.AppField
          name="assignee"
          validators={{
            onChange: v.string('Veld is verplicht'),
          }}
        >
          {(field) => (
            <field.SelectField
              label="Toewijzen aan"
              placeholder="Kies een broker"
              items={brokers.map((broker) => ({
                value: broker.guid,
                label: `${broker.first_name} ${broker.last_name}`,
                meta: {},
              }))}
            />
          )}
        </form.AppField>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <Button
              intent="cta"
              size="large"
              className="self-start"
              type="submit"
            >
              Taak aanmaken
            </Button>
          )}
        </form.Subscribe>
      </fieldset>
    </form>
  );
}

const priorityOptions = [
  {
    value: 'low',
    label: i18n.t('tags.taskPriority.low', { ns: 'system' }),
    meta: {},
  },
  {
    value: 'medium',
    label: i18n.t('tags.taskPriority.medium', { ns: 'system' }),
    meta: {},
  },
  {
    value: 'high',
    label: i18n.t('tags.taskPriority.high', { ns: 'system' }),
    meta: {},
  },
  {
    value: 'critical',
    label: i18n.t('tags.taskPriority.critical', { ns: 'system' }),
    meta: {},
  },
];
